import { Button } from '@/components/ui/button'
import { Trash2, Edit2, UserPlus, GitMerge, X } from 'lucide-react'
import { useMobile } from '@/hooks/use-mobile'
import { useReadOnly } from '@/hooks/use-read-only'
import { cn } from '@/lib/utils'

interface BulkActionsBarProps {
  selectedCount: number
  entityType: string
  onClearSelection: () => void
  onBulkDelete?: () => void
  onBulkUpdate?: () => void
  onBulkAssign?: () => void
  onMerge?: () => void
  isDeleting?: boolean
  isUpdating?: boolean
  isAssigning?: boolean
}

export function BulkActionsBar({
  selectedCount,
  entityType,
  onClearSelection,
  onBulkDelete,
  onBulkUpdate,
  onBulkAssign,
  onMerge,
  isDeleting = false,
  isUpdating = false,
  isAssigning = false,
}: BulkActionsBarProps) {
  const isMobile = useMobile()
  const isReadOnly = useReadOnly()

  if (selectedCount === 0) return null

  // Disable all mutation actions when in read-only mode (impersonation)
  const actionsDisabled = isReadOnly || isDeleting || isUpdating || isAssigning

  return (
    <div
      role="toolbar"
      aria-label="Bulk actions"
      className={cn(
        'fixed z-50 bg-card border border-border shadow-lg rounded-lg animate-in slide-in-from-bottom-2',
        isMobile
          ? 'bottom-0 left-0 right-0 rounded-t-lg rounded-b-none px-4 py-3'
          : 'bottom-6 left-1/2 transform -translate-x-1/2 px-6 py-4'
      )}
    >
      <div className="flex items-center gap-2" aria-live="polite" aria-atomic="true">
        <span className="font-semibold text-foreground">{selectedCount}</span>
        <span className="text-muted-foreground">
          {entityType}
          {selectedCount > 1 ? 's' : ''} selected
        </span>
      </div>

      {!isMobile && (
        <div className="h-6 w-px bg-border" role="separator" aria-orientation="vertical" />
      )}

      <div className={cn('flex items-center gap-2', isMobile && 'flex-1 justify-end')}>
        {onBulkDelete && (
          <Button
            variant="destructive"
            size={isMobile ? 'icon' : 'sm'}
            onClick={onBulkDelete}
            disabled={actionsDisabled}
            aria-label={`Delete ${selectedCount} selected ${entityType}${selectedCount > 1 ? 's' : ''}`}
            title={isReadOnly ? 'Cannot delete while viewing as another user' : undefined}
            className={isMobile ? 'h-9 w-9' : ''}
          >
            <Trash2 className={cn('h-4 w-4', !isMobile && 'mr-2')} aria-hidden="true" />
            {!isMobile && 'Delete'}
          </Button>
        )}

        {onBulkUpdate && (
          <Button
            variant="outline"
            size={isMobile ? 'icon' : 'sm'}
            onClick={onBulkUpdate}
            disabled={actionsDisabled}
            aria-label={`Update ${selectedCount} selected ${entityType}${selectedCount > 1 ? 's' : ''}`}
            title={isReadOnly ? 'Cannot update while viewing as another user' : undefined}
            className={isMobile ? 'h-9 w-9' : ''}
          >
            <Edit2 className={cn('h-4 w-4', !isMobile && 'mr-2')} aria-hidden="true" />
            {!isMobile && 'Update'}
          </Button>
        )}

        {onBulkAssign && (
          <Button
            variant="outline"
            size={isMobile ? 'icon' : 'sm'}
            onClick={onBulkAssign}
            disabled={actionsDisabled}
            aria-label={`Assign ${selectedCount} selected ${entityType}${selectedCount > 1 ? 's' : ''}`}
            title={isReadOnly ? 'Cannot assign while viewing as another user' : undefined}
            className={isMobile ? 'h-9 w-9' : ''}
          >
            <UserPlus className={cn('h-4 w-4', !isMobile && 'mr-2')} aria-hidden="true" />
            {!isMobile && 'Assign'}
          </Button>
        )}

        {onMerge && selectedCount === 2 && (
          <Button
            variant="outline"
            size={isMobile ? 'icon' : 'sm'}
            onClick={onMerge}
            disabled={actionsDisabled}
            aria-label={`Merge 2 selected ${entityType}s`}
            title={isReadOnly ? 'Cannot merge while viewing as another user' : undefined}
            className={isMobile ? 'h-9 w-9' : ''}
          >
            <GitMerge className={cn('h-4 w-4', !isMobile && 'mr-2')} aria-hidden="true" />
            {!isMobile && 'Merge'}
          </Button>
        )}

        {!isMobile && (
          <div className="h-6 w-px bg-border" role="separator" aria-orientation="vertical" />
        )}

        <Button
          variant="ghost"
          size={isMobile ? 'icon' : 'sm'}
          onClick={onClearSelection}
          disabled={isDeleting || isUpdating || isAssigning}
          aria-label="Clear selection"
          className={isMobile ? 'h-9 w-9' : ''}
        >
          <X className={cn('h-4 w-4', !isMobile && 'mr-2')} aria-hidden="true" />
          {!isMobile && 'Clear'}
        </Button>
      </div>
    </div>
  )
}
